<template>
  <video
    ref="videoElement"
    :poster="posterUrl"
    :autoplay="autoplay"
    :muted="muted"
    :controls="controls"
    :loop="loop"
    :preload="preload"
    :crossorigin="crossorigin"
    playsinline
    class="video-player"
    @play="$emit('play')"
    @pause="$emit('pause')"
    @ended="$emit('ended')"
    @timeupdate="$emit('timeupdate', $event)"
    @loadstart="$emit('loadstart', $event)"
    @loadeddata="$emit('loadeddata', $event)"
    @error="$emit('error', $event)"
  >
    <!-- Non-HLS sources only — HLS is attached imperatively via hls.js / native. -->
    <source v-if="!isHls" :src="src" :type="sourceType" />
    Your browser does not support the video tag.
  </video>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";

// hls.js is loaded lazily inside attachHls() — non-video pages don't pay for
// the ~120KB of HLS code, and Safari (which uses native HLS) never loads it.

const props = defineProps({
  src: {
    type: String,
    required: true,
  },
  posterUrl: {
    type: String,
    default: null,
  },
  autoplay: {
    type: Boolean,
    default: false,
  },
  muted: {
    type: Boolean,
    default: false,
  },
  controls: {
    type: Boolean,
    default: true,
  },
  loop: {
    type: Boolean,
    default: false,
  },
  preload: {
    type: String,
    default: "auto",
    validator: (value) => ["none", "metadata", "auto"].includes(value),
  },
  mimeType: {
    type: String,
    default: null,
  },
  // Optional non-HLS URL the player can fall back to if HLS attachment fails
  // (e.g. a Bunny Stream MP4 variant alongside the HLS playlist). The
  // getBunnyAssetUrl function returns this as mp4FallbackUrl for ready videos.
  mp4FallbackUrl: {
    type: String,
    default: null,
  },
  // Set to null to omit the attribute entirely — required for sources whose
  // server doesn't send Access-Control-Allow-Origin (e.g. Bunny Stream's CDN
  // without CORS configured). Default 'anonymous' preserves existing behavior
  // for Firebase Storage-served videos where frame capture to canvas is used.
  crossorigin: {
    type: String,
    default: 'anonymous',
  },
});

defineEmits(["play", "pause", "ended", "timeupdate", "loadstart", "loadeddata", "error"]);

const isHls = computed(() => {
  if (props.mimeType === "application/x-mpegURL" || props.mimeType === "application/vnd.apple.mpegurl") {
    return true;
  }
  return /\.m3u8(\?|$)/i.test(props.src || "");
});

const sourceType = computed(() => {
  if (props.mimeType) return props.mimeType;
  const url = props.src || "";
  if (url.match(/\.mp4(\?|$)/i)) return "video/mp4";
  if (url.match(/\.mov(\?|$)/i)) return "video/quicktime";
  if (url.match(/\.webm(\?|$)/i)) return "video/webm";
  return undefined;
});

const videoElement = ref(null);
let hlsInstance = null;

// Attach an HLS source. Safari plays HLS natively, so prefer that. Other
// browsers go through hls.js. Falls back to mp4FallbackUrl (if provided) on
// fatal error so users on flaky networks or restrictive proxies still get
// a playable video.
async function attachHls(url) {
  const video = videoElement.value;
  if (!video || !url) return;

  // Tear down any prior hls.js instance before attaching a new source.
  if (hlsInstance) {
    hlsInstance.destroy();
    hlsInstance = null;
  }

  if (video.canPlayType("application/vnd.apple.mpegurl")) {
    video.src = url;
    return;
  }

  // Lazy-load hls.js — only fetched the first time a non-Safari client
  // actually needs to play HLS.
  const { default: Hls } = await import("hls.js");

  if (Hls.isSupported()) {
    hlsInstance = new Hls({
      // Bunny segments are short; small back-buffer keeps memory bounded
      // during long playback sessions.
      maxBufferLength: 30,
      backBufferLength: 60,
    });
    hlsInstance.loadSource(url);
    hlsInstance.attachMedia(video);
    hlsInstance.on(Hls.Events.ERROR, (_event, data) => {
      if (!data.fatal) return;
      if (props.mp4FallbackUrl) {
        hlsInstance.destroy();
        hlsInstance = null;
        video.src = props.mp4FallbackUrl;
      }
    });
    return;
  }

  // No HLS support and no fallback — surface to the parent via the existing
  // <video> error event.
  if (props.mp4FallbackUrl) {
    video.src = props.mp4FallbackUrl;
  }
}

function attachSource() {
  if (!videoElement.value || !props.src) return;
  if (isHls.value) {
    attachHls(props.src);
  } else if (hlsInstance) {
    // Switched away from HLS — clean up.
    hlsInstance.destroy();
    hlsInstance = null;
  }
}

onMounted(attachSource);
watch(() => props.src, attachSource);

onBeforeUnmount(() => {
  if (hlsInstance) {
    hlsInstance.destroy();
    hlsInstance = null;
  }
});

defineExpose({
  play: () => videoElement.value?.play(),
  pause: () => videoElement.value?.pause(),
  get currentTime() {
    return videoElement.value?.currentTime || 0;
  },
  set currentTime(time) {
    if (videoElement.value) videoElement.value.currentTime = time;
  },
  get duration() {
    return videoElement.value?.duration || 0;
  },
  get volume() {
    return videoElement.value?.volume || 1;
  },
  set volume(vol) {
    if (videoElement.value) videoElement.value.volume = vol;
  },
  videoElement: () => videoElement.value,
});
</script>

<style lang="scss">
.video-player {
  width: 100%;
  height: auto;
  border-radius: 0.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  background-color: #000;
}

@media (max-width: 768px) {
  .video-player {
    max-width: 100%;
    border-radius: 0.25rem;
  }
}
</style>
